import { useState } from "react";
import { toast } from "sonner";
import { Building2, BadgeCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import { CompanyStatusBadge } from "../../components/common/Badges";
import ReasonDialog from "../../components/admin/ReasonDialog";
import VerifyDialog from "../../components/admin/VerifyDialog";
import { COMPANY_STATUS, ORG_TYPE, ROLE, fmtDate } from "../../lib/labels";
import { DECISION, VERIFY_CHECKS, usePlatformCan } from "../../lib/platform";

const Decisions = ({ items }) => (
  <div data-testid="admin-company-decisions"><p className="eyebrow mb-2">Décisions administratives</p>
    {!items.length && <p className="text-xs text-slate-500">Aucune décision enregistrée.</p>}
    <ul className="max-h-48 space-y-2 overflow-y-auto text-xs">{items.map((d) => (
      <li key={d.id} className="rounded-md border border-slate-200 p-2" data-testid="admin-company-decision">
        <p><b>{DECISION[d.action] || d.action}</b> · {fmtDate(d.created_at)} · {d.actor_name}</p>
        {d.reason && <p className="mt-0.5 text-slate-600">Motif : {d.reason}</p>}
      </li>))}</ul>
  </div>
);

const Detail = ({ id, onClose }) => {
  const { data, loading, error } = useApi(`/admin/companies/${id}`);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="admin-company-detail-dialog">
        <DialogHeader><DialogTitle>{data?.name || "Entreprise"}</DialogTitle></DialogHeader>
        {loading && <Loader />}
        {error && <ErrorState message={error} />}
        {data && (
          <div className="space-y-4 text-sm">
            <p className="text-slate-600">{ORG_TYPE[data.org_type]} · {data.sector} · {data.city}, {data.country}</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["agencies", "Agences"], ["services", "Services"], ["qrcodes", "QR codes"], ["feedbacks", "Feedbacks"]].map(([k, l]) => (
                <div key={k} className="rounded-lg bg-slate-50 p-2"><p className="font-display text-lg font-bold">{data.counts[k]}</p><p className="text-xs text-slate-500">{l}</p></div>
              ))}
            </div>
            <div data-testid="admin-company-verification"><p className="eyebrow mb-1">Vérification</p>
              {data.verification ? <p className="text-xs">{data.verification.result === "verified" ? "Vérifiée" : "Informations manquantes"} par {data.verification.by} le {fmtDate(data.verification.at)} · {data.verification.checks.map((c) => VERIFY_CHECKS[c]).join(", ") || "aucun point coché"}</p>
                : <p className="text-xs text-slate-500">Non vérifiée.</p>}
            </div>
            <div><p className="eyebrow mb-2">Membres</p>{data.members.map((m) => <p key={m.id}>{m.name} — {m.email} <span className="text-slate-400">({ROLE[m.role]}{m.status === "inactive" ? ", désactivé" : ""})</span></p>)}</div>
            <div className="space-y-0.5 text-slate-600" data-testid="admin-company-contact">
              <p>Raison sociale : {data.legal_name || "—"} · Email : {data.email || "—"} ({data.email_verified ? "confirmé" : "non confirmé"})</p>
              <p>Tél. : {data.phone || "—"} · Adresse : {data.address || "—"} · Site : {data.website || "—"}</p>
            </div>
            <Decisions items={data.decisions} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ACTIONS = { refuse: ["draft", "Refuser la demande", "Refuser"], suspend: ["suspended", "Suspendre l'entreprise", "Suspendre"],
  terminate: ["terminated", "Résilier l'entreprise", "Résilier"], reactivate: ["active", "Réactiver l'entreprise", "Réactiver"] };

const RowActions = ({ c, setAction, setVerify, approve }) => {
  const can = usePlatformCan();
  return (
    <td className="space-x-1 whitespace-nowrap px-4 py-3 text-right">
      {can("companies:verify") && <Button size="sm" variant="ghost" onClick={() => setVerify(c)} data-testid={`admin-company-verify-${c.id}`}>Vérifier</Button>}
      {can("companies:approve") && c.status === "pending" && <Button size="sm" onClick={() => approve(c)} data-testid={`admin-company-activate-${c.id}`}>Accepter</Button>}
      {can("companies:approve") && c.status === "pending" && <Button size="sm" variant="outline" onClick={() => setAction({ c, k: "refuse" })} data-testid={`admin-company-refuse-${c.id}`}>Refuser</Button>}
      {can("companies:suspend") && ["suspended", "terminated"].includes(c.status) && <Button size="sm" onClick={() => setAction({ c, k: "reactivate" })} data-testid={`admin-company-reactivate-${c.id}`}>Réactiver</Button>}
      {can("companies:suspend") && c.status === "active" && <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setAction({ c, k: "suspend" })} data-testid={`admin-company-suspend-${c.id}`}>Suspendre</Button>}
      {can("companies:suspend") && c.status !== "terminated" && <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => setAction({ c, k: "terminate" })} data-testid={`admin-company-terminate-${c.id}`}>Résilier</Button>}
    </td>
  );
};

export default function AdminCompanies() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const [detail, setDetail] = useState(null);
  const [verify, setVerify] = useState(null);
  const [action, setAction] = useState(null);
  const { data, loading, error, reload } = useApi(`/admin/companies?status=${status}&q=${encodeURIComponent(applied)}`);

  const setCompanyStatus = async (c, s, reason = "") => {
    try { await api.patch(`/admin/companies/${c.id}`, { status: s, reason }); toast.success(`${c.name} : ${COMPANY_STATUS[s].label}`); setAction(null); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Entreprises" description="Consultation, vérification, approbation, suspension et réactivation. Chaque décision est motivée et historisée." />
      <div className="mb-6 flex flex-col gap-2 sm:flex-row">
        <form className="flex-1" onSubmit={(e) => { e.preventDefault(); setApplied(q); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une entreprise…" data-testid="admin-companies-search" />
        </form>
        <select className="field-select sm:w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-companies-status-filter">
          <option value="">Tous statuts</option>
          {Object.entries(COMPANY_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
      </div>
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.length && <EmptyState icon={Building2} title="Aucune entreprise" />}
      {data && data.length > 0 && (
        <div className="card-surface overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3">Pays</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Feedbacks</th><th className="px-4 py-3">Membres</th><th className="px-4 py-3">Créée le</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y">
              {data.map((c) => (
                <tr key={c.id} data-testid={`admin-company-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <button className="flex items-center gap-1 text-left font-medium hover:text-indigo-700" onClick={() => setDetail(c.id)} data-testid={`admin-company-open-${c.id}`}>
                      {c.name}{c.verification?.result === "verified" && <BadgeCheck className="h-4 w-4 text-emerald-600" data-testid={`admin-company-verified-${c.id}`} />}
                    </button>
                    <p className="text-xs text-slate-500">{ORG_TYPE[c.org_type]} · {c.sector}</p>
                  </td>
                  <td className="px-4 py-3">{c.country}</td>
                  <td className="px-4 py-3"><CompanyStatusBadge value={c.status} /></td>
                  <td className="px-4 py-3">{c.feedback_count}</td>
                  <td className="px-4 py-3">{c.member_count}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(c.created_at, false)}</td>
                  <RowActions c={c} setAction={setAction} setVerify={setVerify} approve={(co) => setCompanyStatus(co, "active")} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && <Detail id={detail} onClose={() => setDetail(null)} />}
      {verify && <VerifyDialog company={verify} onClose={() => setVerify(null)} onDone={() => { setVerify(null); reload(); }} />}
      {action && <ReasonDialog testId={`admin-${action.k}`} title={`${ACTIONS[action.k][1]} — ${action.c.name}`} label={ACTIONS[action.k][2]}
        variant={action.k === "reactivate" ? "default" : "destructive"}
        onClose={() => setAction(null)} onConfirm={(r) => setCompanyStatus(action.c, ACTIONS[action.k][0], r)} />}
    </div>
  );
}
